import { ScholarLayout } from "@jambushrushti/components/scholar/ScholarLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Link } from "react-router-dom";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";
import {
  Users,
  MessageSquare,
  Calendar,
  Sparkles,
  BookOpen,
  FileText,
  TrendingUp,
  Bell,
  ArrowRight,
  Search,
  Globe,
} from "lucide-react";

const quickActions = [
  {
    icon: Users,
    label: "Browse Scholars",
    href: "/scholar/directory",
    color: "bg-blue-500/10 text-blue-500",
  },
  {
    icon: MessageSquare,
    label: "Start Discussion",
    href: "/scholar/chat",
    color: "bg-green-500/10 text-green-500",
  },
  {
    icon: Sparkles,
    label: "AI Assistant",
    href: "/scholar/ai-tools",
    color: "bg-purple-500/10 text-purple-500",
  },
  {
    icon: Calendar,
    label: "Upcoming Events",
    href: "/scholar/events",
    color: "bg-orange/10 text-orange",
  },
];

const researchTools = [
  {
    icon: Search,
    label: "SodhSanchay",
    description: "Store research notes",
    href: "/research/sodhsanchay",
  },
  {
    icon: FileText,
    label: "Śabdasaṅgraha",
    description: "PDF search",
    href: "/research/shabdasangraha",
  },
  {
    icon: Globe,
    label: "Shastrasandarbha",
    description: "Global library",
    href: "/research/shastrasandarbha",
  },
];

const recentActivities = [
  {
    type: "discussion",
    title: "New discussion on Jain Philosophy",
    time: "2 hours ago",
    user: "Dr. Sharma",
  },
  {
    type: "event",
    title: "Webinar: Ancient Texts Digitization",
    time: "5 hours ago",
    user: "System",
  },
  {
    type: "collaboration",
    title: "Research collaboration request",
    time: "1 day ago",
    user: "Prof. Mehta",
  },
  {
    type: "paper",
    title: "New paper submission received",
    time: "2 days ago",
    user: "Dr. Patel",
  },
];

export default function ScholarDashboard() {
  const { user } = useAdminAuth();

  return (
    <ScholarLayout title="Dashboard">
      <div className="space-y-4 sm:space-y-6">
        {/* Welcome Section */}
        <div className="rounded-xl bg-gradient-to-r from-primary to-primary/80 p-4 text-white sm:rounded-2xl sm:p-6">
          <h2 className="font-heading mb-2 text-xl font-bold sm:text-2xl">
            Welcome back, {user?.name?.split(" ")[0] || "Scholar"}!
          </h2>
          <p className="mb-4 text-sm text-white/80 sm:text-base">
            Continue your research journey and collaborate with fellow scholars.
          </p>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Button
              asChild
              variant="secondary"
              size="sm"
              className="bg-white text-primary hover:bg-white/90"
            >
              <Link to="/scholar/feed">View Community Feed</Link>
            </Button>
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="border-white/30 text-white hover:bg-white/10"
            >
              <Link to="/research">Research Tools</Link>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          <Card>
            <CardContent className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-500/10 sm:h-12 sm:w-12">
                <Users className="h-5 w-5 text-blue-500 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xl font-bold text-foreground sm:text-2xl">
                  156
                </p>
                <p className="text-xs text-muted-foreground sm:text-sm">
                  Active Scholars
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-green-500/10 sm:h-12 sm:w-12">
                <MessageSquare className="h-5 w-5 text-green-500 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xl font-bold text-foreground sm:text-2xl">
                  24
                </p>
                <p className="text-xs text-muted-foreground sm:text-sm">
                  Discussions
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4">
              <div className="bg-orange/10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12">
                <Calendar className="text-orange h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xl font-bold text-foreground sm:text-2xl">
                  8
                </p>
                <p className="text-xs text-muted-foreground sm:text-sm">
                  Events
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-purple-500/10 sm:h-12 sm:w-12">
                <FileText className="h-5 w-5 text-purple-500 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xl font-bold text-foreground sm:text-2xl">
                  42
                </p>
                <p className="text-xs text-muted-foreground sm:text-sm">
                  Papers
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Research Tools Quick Access */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base sm:text-lg">
              Research Tools
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {researchTools.map(tool => (
                <Link
                  key={tool.href}
                  to={tool.href}
                  className="group flex items-center gap-3 rounded-xl border border-border bg-background p-3 transition-colors hover:bg-muted sm:p-4"
                >
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <tool.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">
                      {tool.label}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {tool.description}
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 sm:gap-6 lg:grid-cols-3">
          {/* Quick Actions */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-base sm:text-lg">
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 sm:space-y-3">
              {quickActions.map(action => (
                <Link
                  key={action.href}
                  to={action.href}
                  className="group flex items-center gap-3 rounded-xl p-3 transition-colors hover:bg-muted"
                >
                  <div
                    className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${action.color}`}
                  >
                    <action.icon className="h-5 w-5" />
                  </div>
                  <span className="flex-1 text-sm font-medium">
                    {action.label}
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
                </Link>
              ))}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base sm:text-lg">
                Recent Activity
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                asChild
                className="text-xs sm:text-sm"
              >
                <Link to="/scholar/feed">View All</Link>
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                {recentActivities.map((activity, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 rounded-xl p-2 transition-colors hover:bg-muted/50 sm:gap-4 sm:p-3"
                  >
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 sm:h-10 sm:w-10">
                      {activity.type === "discussion" && (
                        <MessageSquare className="h-4 w-4 text-primary sm:h-5 sm:w-5" />
                      )}
                      {activity.type === "event" && (
                        <Calendar className="text-orange h-4 w-4 sm:h-5 sm:w-5" />
                      )}
                      {activity.type === "collaboration" && (
                        <Users className="h-4 w-4 text-green-500 sm:h-5 sm:w-5" />
                      )}
                      {activity.type === "paper" && (
                        <FileText className="h-4 w-4 text-purple-500 sm:h-5 sm:w-5" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">
                        {activity.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {activity.user} · {activity.time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Events Preview */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base sm:text-lg">
              Upcoming Events
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              asChild
              className="text-xs sm:text-sm"
            >
              <Link to="/scholar/events">View All</Link>
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
              <div className="hover:border-orange/50 rounded-xl border border-border p-3 transition-colors sm:p-4">
                <div className="text-orange mb-2 flex items-center gap-2 text-xs sm:text-sm">
                  <Calendar className="h-4 w-4" />
                  Dec 15, 2025
                </div>
                <h3 className="mb-1 text-sm font-semibold">
                  Jain Manuscript Workshop
                </h3>
                <p className="text-xs text-muted-foreground">
                  Learn preservation techniques
                </p>
              </div>
              <div className="hover:border-orange/50 rounded-xl border border-border p-3 transition-colors sm:p-4">
                <div className="text-orange mb-2 flex items-center gap-2 text-xs sm:text-sm">
                  <Calendar className="h-4 w-4" />
                  Dec 20, 2025
                </div>
                <h3 className="mb-1 text-sm font-semibold">
                  Research Methodology Webinar
                </h3>
                <p className="text-xs text-muted-foreground">
                  Advanced research techniques
                </p>
              </div>
              <div className="hover:border-orange/50 rounded-xl border border-border p-3 transition-colors sm:col-span-2 sm:p-4 lg:col-span-1">
                <div className="text-orange mb-2 flex items-center gap-2 text-xs sm:text-sm">
                  <Calendar className="h-4 w-4" />
                  Jan 5, 2026
                </div>
                <h3 className="mb-1 text-sm font-semibold">
                  Annual Scholars Meet
                </h3>
                <p className="text-xs text-muted-foreground">
                  Network with fellow researchers
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </ScholarLayout>
  );
}
